import type {Component, Editor, PropertyProps} from 'grapesjs';
import type {PrimaryConfigOptions} from '@/email-maker/grapesjs-email-editor-plugin/src';
import {USER_PREMIUM_LEVELS} from '@Utils/user.util';

export interface ModificationItem {
  property?: string;
  iconClass: string;
  label: string;
  credits?: number;
  action?: string;
  callback: (editorInstance: Editor, component: Component, el: HTMLElement, container: HTMLElement) => Promise<void>;
  subOptions?: ModificationSubOption[];
}

export interface ModificationSubOption {
  label: string;
  icon: string;
  cb: () => void;
}

export interface ModListProps extends PropertyProps {
  component?: Component;
  class: string;
  list: ModificationItem[];
}

export default (opt: PrimaryConfigOptions): void => {
  const {editor} = opt;
  const styleManager = editor.StyleManager;

  styleManager.addType(window.PMW.emails.styles.ID_MOD_LIST, {
    /**
     * Creates a modification list element
     */
    create: function (data) {
      const {change} = data;
      const props = data.props as ModListProps;
      props.component = props.component ?? editor.getSelected();
      const selected = props.component;

      if (!selected) {
        return document.createElement('div');
      }

      const isUserPremium = window.PMW.getUserPremiumLevel() > USER_PREMIUM_LEVELS.PAYG;
      const container = document.createElement('ul');
      const wrapper = document.createElement('div');

      container.className = props.type + ' mod-list-container list-two ' + props.class;
      wrapper.className = 'mod-list-wrapper';

      props.list.forEach((item) => {
        const li = document.createElement('li');
        const div = document.createElement('div');
        const icon = document.createElement('i');
        const label = document.createElement('span');
        const isPropertySet = item.property && selected.get(item.property);

        li.className = 'mod-list-item _cursor-pointer hover-transition-all';
        div.className = 'mod-list-item-div _full-height';
        div.title = item.label;
        icon.className = 'mod-list-item-icon icon ' + item.iconClass;
        label.className = 'mod-list-item-label body-sm-regular ' + (item.label ? '' : ' _hidden');
        label.innerHTML = item.label;

        if (isPropertySet) {
          li.className += ' -selected';
        }

        if (
          (item.action && item.action === window.PMW.emails.commands.REMOVE_BACKGROUND) ||
          (item.action === window.PMW.emails.commands.mediaModals.OPEN_AI_IMAGES && !isUserPremium)
        ) {
          const tagDiv = document.createElement('div');
          tagDiv.className = 'tag _position-absolute';
          tagDiv.innerHTML = renderTag(window.PMW.emails.NUM_REMOVE_BACKGROUND_CREDITS, isUserPremium);
          li.appendChild(tagDiv);
        }

        if (item.subOptions) {
          const subOptionsContainer = getModItemSubOptions(item, container);
          wrapper.appendChild(subOptionsContainer);
        }

        div.appendChild(icon);
        div.appendChild(label);
        li.appendChild(div);
        container.appendChild(li);
        wrapper.prepend(container);

        div.addEventListener('click', async (): Promise<void> => {
          if (typeof item.callback !== 'function') {
            return;
          }

          await item.callback(editor, selected, li, wrapper);
          change({
            li: li,
            component: selected,
            property: item.property,
          });
        });
      });

      return wrapper;
    },
    emit(data, eventData: {li: HTMLElement; component: Component; property?: string}) {
      const {li, component, property} = eventData;
      li.className = li.className.replace('-selected', '').trim();

      if (property && component.get(property)) {
        li.className += ' -selected';
      }
    },
    update() {
      // No-op
    },
  });

  function getModItemSubOptions(listItem: ModificationItem, modListContainer: HTMLElement): HTMLElement {
    const subOptionsContainer = document.createElement('div');
    subOptionsContainer.className = 'mod-list-suboptions-container bg-neutral-1 spacing-p-3 radius-4 _hidden';

    const subOptionsHeader = getSubOptionsHeader(listItem, modListContainer, subOptionsContainer);
    const subOptions = getSubOptionsList(listItem);

    subOptionsContainer.appendChild(subOptionsHeader);
    subOptionsContainer.appendChild(subOptions);

    return subOptionsContainer;
  }

  function getSubOptionsHeader(listItem: ModificationItem, modListContainer: HTMLElement, subOptionsContainer: HTMLElement): HTMLElement {
    const subOptionsHeader = document.createElement('div');
    subOptionsHeader.className = 'flex-h-row flex-content-between spacing-m-b-4';

    const subOptionsHeaderTitle = document.createElement('div');
    subOptionsHeaderTitle.className = 'flex-center';

    const subOptionsIcon = document.createElement('i');
    subOptionsIcon.className = 'pmw-icon size-icon-20 content-body spacing-m-r-3 ' + listItem.iconClass;

    const subOptionsHeaderLabel = document.createElement('label');
    subOptionsHeaderLabel.className = 'body-md-regular content-body';
    subOptionsHeaderLabel.innerHTML = listItem.label;

    const subOptionsHeaderCloseCTA = document.createElement('i');
    subOptionsHeaderCloseCTA.className = 'pmw-icon size-icon-20 -square -ghost-uncolored icon-close content-body';

    subOptionsHeaderCloseCTA.addEventListener('click', function () {
      subOptionsContainer.classList.add('_hidden');
      modListContainer.classList.remove('_hidden');
    });

    subOptionsHeaderTitle.appendChild(subOptionsIcon);
    subOptionsHeaderTitle.appendChild(subOptionsHeaderLabel);
    subOptionsHeader.appendChild(subOptionsHeaderTitle);
    subOptionsHeader.appendChild(subOptionsHeaderCloseCTA);

    return subOptionsHeader;
  }

  function getSubOptionsList(listItem: ModificationItem): HTMLElement {
    const subOptions = document.createElement('ul');

    listItem.subOptions?.forEach((option) => {
      const subOption = document.createElement('li');
      subOption.className = 'flex-center flex-v-row mod-list-suboption spacing-p-4 radius-4 bg-neutral-0 border-s-dark _cursor-pointer hover-transition-all';

      const icon = document.createElement('i');
      icon.className = 'pmw-icon size-icon-24 content-body ' + option.icon;

      const label = document.createElement('span');
      label.className = 'body-md-regular content-body mod-list-suboption-label';
      label.innerHTML = option.label;

      subOption.addEventListener('click', function () {
        if (typeof option.cb !== 'function') {
          return;
        }

        option.cb();
      });

      subOption.appendChild(icon);
      subOption.appendChild(label);
      subOptions.appendChild(subOption);
    });

    return subOptions;
  }

  /**
   * Gets HTML tag new and credit count
   */
  function renderTag(removeBackgroundCredits: number, isUserPremium: boolean): string {
    const hideRemainingBgRemovals = removeBackgroundCredits && (removeBackgroundCredits > -1);
    const premiumTagDiv = `<i class="color-premium icon-crown pmw-icon -icon-only size-icon-20"></i>`;
    const creditsDiv = hideRemainingBgRemovals ?
      `<div class="bg-removals-tag js-bg-credits body-sm-bold radius-round content-body-white _fit-width flex-center bg-secondary-150" rel="tooltip" data-is-modern-tooltip="1"
                 title="${window.i18next.t('pmwjs_credits_cost_and_remaining_info', {creditsRemaining: removeBackgroundCredits, creditsCost: 1})}">
                    ${formatToNearestThousand(removeBackgroundCredits)}
                 </div>`
      : '';

    return isUserPremium ? creditsDiv : premiumTagDiv;
  }

  function formatToNearestThousand(credits: number): string {
    if (credits > 999) {
      const temp = credits / 1000;
      return `${temp.toFixed(0)}k`;
    }

    return String(credits);
  }
};